import type {
  EnrichmentHandle,
  ProviderEnrichmentData,
  ProviderMention,
} from '@/services/types/treatment-types';

/**
 * Type guard to check if a mention has modern enrichment data.
 */
export function hasModernEnrichment(
  mention: ProviderMention,
): mention is ProviderMention & { enrichment: ProviderEnrichmentData } {
  return mention.enrichment !== null && mention.enrichment !== undefined;
}

/**
 * Type guard to check if a mention has legacy enrichment data.
 */
export function hasLegacyEnrichment(mention: ProviderMention): boolean {
  return (
    mention.enrichment === null &&
    mention.enrichment_data !== null &&
    mention.enrichment_data !== undefined &&
    'primary_hit' in mention.enrichment_data
  );
}

/**
 * Returns modern enrichment data if available.
 * Legacy enrichment data (Perplexity format with primary_hit) is intentionally
 * not displayed as it contains verbose summaries that create noise for users.
 */
export function normalizeEnrichmentData(
  mention: ProviderMention,
): ProviderEnrichmentData | null {
  if (hasModernEnrichment(mention)) {
    return mention.enrichment;
  }

  // Legacy enrichment data is intentionally not converted/displayed
  return null;
}

/**
 * Gets enrichment data from the first mention that has modern enrichment.
 * Legacy enrichment data is intentionally excluded.
 */
export function getEnrichmentFromMentions(
  mentions: ProviderMention[] | undefined,
): ProviderEnrichmentData | null {
  if (!mentions || mentions.length === 0) return null;

  // Only look for modern enrichment format
  const mentionWithEnrichment = mentions.find((m) => m.enrichment);

  if (!mentionWithEnrichment) return null;

  return normalizeEnrichmentData(mentionWithEnrichment);
}

/**
 * Gets handles marked as preferred from enrichment data.
 */
export function getPreferredHandles(
  enrichment: ProviderEnrichmentData | null,
): EnrichmentHandle[] {
  if (!enrichment?.handles) return [];
  return enrichment.handles.filter((h) => h.preferred === true && h.value);
}

/**
 * Sorts handles with preferred first.
 */
export function sortHandlesByPreferred(
  handles: EnrichmentHandle[],
): EnrichmentHandle[] {
  return [...handles].sort((a, b) => {
    if (a.preferred && !b.preferred) return -1;
    if (!a.preferred && b.preferred) return 1;
    return 0;
  });
}

/**
 * Gets the confidence tier for styling purposes.
 */
export function getConfidenceTier(
  confidence: number,
): 'high' | 'medium' | 'low' {
  if (confidence >= 0.7) return 'high';
  if (confidence >= 0.4) return 'medium';
  return 'low';
}

/**
 * Formats confidence as a percentage string.
 */
export function formatConfidencePercent(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}
